<script lang="ts">
  import type { IDocumentNode, PartialNode } from "@shared/common/node/type";
  import type { ICampaignSetting } from "@shared/common/types/campaign";
  import fontFaceOnLoad from "fontfaceonload";
  import { getContext } from "svelte";
  import { getStore } from "../context";
  import { getDocumentFontKeySet, parseFontKey } from "../util/common";

  const { nodeData, childrenNodeData } = getStore();
  const campaignSetting = getContext<ICampaignSetting>("campaignSetting");

  export let onLoad: () => void;

  const FONT_STYLESHEET_VERSION_BY_FAMILY: Record<string, string> = {
    "PretendardVariable-v1": "dynamic-subset-20260510",
  };

  let fontLoaded = 0;

  function getFontStylesheetHref(fontFamily: string) {
    const version = FONT_STYLESHEET_VERSION_BY_FAMILY[fontFamily];
    const href = `https://upload.codenbutter.com/fonts/${fontFamily}/stylesheet.css`;
    return version ? `${href}?v=${version}` : href;
  }

  function hasFontLoaded(fontFamily: string) {
    return (
      window._cnbFontsLoaded?.[fontFamily] &&
      document.querySelector(`link[href="${getFontStylesheetHref(fontFamily)}"]`)
    );
  }

  function onOneFontLoaded() {
    fontLoaded++;
  }

  function getFontFamilies(fontKeySet: Set<string>) {
    const fontFamilies = new Set<string>();

    for (const fontKey of fontKeySet) {
      const fontFamily = parseFontKey(fontKey).family;
      if (hasFontLoaded(fontFamily)) {
        continue;
      }
      fontFamilies.add(fontFamily);
      window._cnbFontsLoaded = {
        ...window._cnbFontsLoaded,
        [fontFamily]: true,
      };
    }

    return [...fontFamilies];
  }

  function getKeySetFromDocumentNodeList(
    documentNodeList: PartialNode<IDocumentNode>[]
  ) {
    const fontKeySet = new Set<string>();
    for (const documentNode of documentNodeList) {
      const documentFontKeySet = getDocumentFontKeySet(
        documentNode,
        campaignSetting.language
      );
      for (const fontKey of documentFontKeySet) {
        fontKeySet.add(fontKey);
      }
    }
    return fontKeySet;
  }

  $: fontKeySet = getKeySetFromDocumentNodeList([
    ...($nodeData ? [$nodeData] : []),
    ...($childrenNodeData || []),
  ]);

  $: {
    fontLoaded = 0;
    fontKeySet.forEach((fontKey) => {
      const { family, style } = parseFontKey(fontKey);
      if (hasFontLoaded(family)) {
        onOneFontLoaded();
        return;
      }

      fontFaceOnLoad(family, {
        glyphs: "ab123AB한글쀑",
        success: onOneFontLoaded,
        error: onOneFontLoaded,
        style,
      });
    });
  }

  $: noFont = $nodeData && fontKeySet.size === 0;

  $: if ($nodeData && (noFont || fontLoaded === fontKeySet.size)) {
    onLoad();
  }

  $: fontFamilies = getFontFamilies(fontKeySet);
</script>

<svelte:head>
  {#each fontFamilies as fontFamily}
    <link
      rel="stylesheet"
      href={getFontStylesheetHref(fontFamily)}
    />
  {/each}
</svelte:head>
